/**
 * Boss Towers — interactive, turn-based battle game.
 * Mirrors the Discord bot's `/battletowers` command.
 *
 * Flow:
 *   1. Overview grid — 7 towers as cards with CLEARED/UNLOCKED/LOCKED state.
 *   2. Click an unlocked tower → pal-picker (your roster, filtered & sorted by level).
 *   3. Click "Fight!" → turn-based combat with skill buttons + HP bars.
 *   4. Win → victory card (skulls, XP, capture roll) | Lose → defeat card + cooldown.
 *
 * The site already wraps the page in <Layout>, which surfaces the
 * global "transfer received" + win-flash cinematic. We additionally
 * fire the local <PayoutFlash> on a tower clear so the celebration is
 * specific to this game ("TOWER N CLEARED").
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader } from "@/components/Atoms";
import { PayoutFlash } from "@/components/PayoutFlash";
import { PalPortrait } from "@/components/PalAssets";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import {
  Trophy, MapPin, Zap, Heart, Shield, ShieldAlert,
  Lock, CheckCircle2, Swords, ChevronLeft, Skull, Star,
} from "lucide-react";

const ASSETS = `${process.env.REACT_APP_BACKEND_URL}/api/assets`;

export default function Towers() {
  const toast = useToast();
  const { user, refreshProfile } = useAuth();
  const [state, setState] = useState(null);          // GET /palgame/towers/state response
  const [loading, setLoading] = useState(true);
  const [selectedTower, setSelectedTower] = useState(null);
  const [battle, setBattle] = useState(null);        // active battle session
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [defeatFlash, setDefeatFlash] = useState(null);
  const battleLogRef = useRef(null);

  const loadState = async () => {
    setLoading(true);
    try {
      if (!user) {
        // Guest fallback — show the read-only tower list so the page
        // works without auth too (used to be the only behaviour).
        const { data } = await api.get("/towers/list");
        setState({
          highest_cleared: 0,
          cooldown_seconds: 0,
          pals: [],
          towers: (data.towers || []).map((t) => ({ ...t, status: "locked" })),
          _guest: true,
        });
        return;
      }
      const { data } = await api.get("/palgame/towers/state");
      setState(data);
    } catch (e) {
      toast.error(apiError(e, "Couldn't load tower state"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadState(); /* eslint-disable-next-line */ }, [user?.user_id]);

  // Refresh tower state when an admin updates the tower-battle cooldown
  // so the on-screen countdown reflects the new duration immediately.
  useEffect(() => {
    const onCdUpdate = (e) => {
      const { key } = e.detail || {};
      if (!key || key === "cooldown_tower_battle") {
        loadState();
      }
    };
    window.addEventListener("chillets:cooldown-update", onCdUpdate);
    return () => window.removeEventListener("chillets:cooldown-update", onCdUpdate);
    /* eslint-disable-next-line */
  }, []);

  // Auto-scroll the battle log to the bottom on every new event.
  useEffect(() => {
    if (battleLogRef.current) {
      battleLogRef.current.scrollTop = battleLogRef.current.scrollHeight;
    }
  }, [battle?.log?.length]);

  // ---- Open tower / start battle ----------------------------------------
  const openTower = (t) => {
    if (t.status === "locked") {
      toast.error(`Clear Tower ${t.tower - 1} first.`);
      return;
    }
    if (!user) {
      toast.error("Sign in to challenge a tower.");
      return;
    }
    setSelectedTower(t);
  };

  const startBattle = async (palName) => {
    if (!selectedTower) return;
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/towers/start", {
        tower: selectedTower.tower,
        pal_name: palName,
      });
      setBattle({ ...data, log: [], finished: false, result: null });
    } catch (e) {
      toast.error(apiError(e, "Couldn't start battle"));
    } finally {
      setBusy(false);
    }
  };

  const useSkill = async (idx) => {
    if (!battle || busy || battle.finished) return;
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/towers/action", {
        session_id: battle.session_id,
        skill_index: idx,
      });
      setBattle((prev) => ({
        ...prev,
        boss: { ...prev.boss, hp: data.boss.hp },
        player: { ...prev.player, hp: data.player.hp },
        log: [...(prev.log || []), ...(data.events || [])],
        finished: !!data.finished,
        result: data.result || null,
      }));
      if (data.finished && data.result) {
        if (data.result.won) {
          setFlash({
            amount: data.result.skulls,
            label: `TOWER ${selectedTower.tower} CLEARED`,
          });
          if (data.result.captured) {
            toast.success(`Captured ${data.result.boss_pal_name}!`);
          }
        } else {
          toast.error("Defeated — better luck next time.");
          setDefeatFlash({ label: `TOWER ${selectedTower.tower} DEFEAT` });
        }
        // Refresh user-facing balance + tower state in the background.
        refreshProfile?.();
        loadState();
      }
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  const exitBattle = () => {
    setBattle(null);
    setSelectedTower(null);
  };

  // ---- Render ----------------------------------------------------------
  if (loading) return <Loader label="Climbing the towers…" />;

  if (battle) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 fade-up">
        <BattleArena
          battle={battle}
          tower={selectedTower}
          busy={busy}
          onSkill={useSkill}
          onExit={exitBattle}
          logRef={battleLogRef}
        />
        <PayoutFlash
          show={flash !== null}
          variant="skull"
          amount={flash?.amount || 0}
          label={flash?.label || "TOWER CLEARED"}
          onDone={() => setFlash(null)}
        />
        <PayoutFlash
          show={defeatFlash !== null}
          variant="defeat"
          amount={defeatFlash?.amount || 0}
          label={defeatFlash?.label || "DEFEAT"}
          duration={1400}
          onDone={() => setDefeatFlash(null)}
        />
      </div>
    );
  }

  if (selectedTower) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 fade-up">
        <PalPicker
          tower={selectedTower}
          pals={state?.pals || []}
          busy={busy}
          cooldownSeconds={state?.cooldown_seconds || 0}
          onPick={startBattle}
          onCancel={() => setSelectedTower(null)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="towers-page">
      <PageHeader
        title="Boss Towers"
        subtitle="Seven trials. Seven bosses. Climb to the top of the Palpagos Islands."
      />
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="text-sm text-[var(--muted)]" data-testid="tower-progress">
          Highest cleared:{" "}
          <span className="glow-teal font-semibold">
            {(state?.highest_cleared ?? 0)} / 7
          </span>
        </div>
        {state?.cooldown_seconds > 0 && (
          <div className="text-xs text-[var(--muted)]" data-testid="tower-cooldown">
            Next battle in {fmtMmSs(state.cooldown_seconds)}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {(state?.towers || []).map((t, i) => (
          <TowerCard key={t.tower} t={t} idx={i} onOpen={openTower} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tower card with status overlay
// ---------------------------------------------------------------------------
function TowerCard({ t, idx, onOpen }) {
  const locked = t.status === "locked";
  const cleared = t.status === "cleared";
  const badge =
    cleared ? { label: "CLEARED", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 } :
    locked ? { label: "LOCKED",   cls: "bg-white/5 text-[var(--muted)] border-white/10", Icon: Lock } :
    { label: "UNLOCKED", cls: "bg-[rgba(0,229,255,0.10)] text-[var(--teal)] border-[var(--teal)]/40", Icon: Swords };

  return (
    <button
      onClick={() => onOpen(t)}
      disabled={locked}
      className={`glass-card p-5 text-left transition-all relative overflow-hidden ${
        locked ? "opacity-60 cursor-not-allowed" : "hover:-translate-y-1"
      }`}
      style={{ animationDelay: `${idx * 60}ms` }}
      data-testid={`tower-card-${t.tower}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs text-[var(--muted)]">Tower {t.tower}</div>
          <h3 className="text-xl glow-teal" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            {t.boss_name}
          </h3>
          <div className="text-xs text-[var(--muted)] mt-1 flex items-center gap-1">
            <MapPin size={11} /> {t.location} · Lv {t.level}
          </div>
        </div>
        <span className={`badge ${badge.cls} flex items-center gap-1`}>
          <badge.Icon size={11} /> {badge.label}
        </span>
      </div>
      <div className="flex gap-3 items-center">
        <img
          src={`${ASSETS}/${t.image}`}
          alt={t.boss_name}
          className="w-20 h-20 object-cover rounded-lg flex-shrink-0 bg-black/30"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
        />
        <div className="text-xs space-y-1 flex-1">
          <div className="flex items-center gap-2">
            <Zap size={12} className="text-amber-300" /> Boss type:{" "}
            <span className="text-[var(--bone)]">
              {(t.elements || []).join(" / ") || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldAlert size={12} className="text-[var(--gold)]" /> Weakness:{" "}
            <span className="text-[var(--bone)]">{t.weakness}</span>
          </div>
          <div className="flex items-center gap-2">
            <Skull size={12} className="text-[var(--teal)]" /> Reward:{" "}
            <span className="glow-gold inline-flex items-center gap-1">{t.reward_skulls.toLocaleString()} <ChilletCoinIcon size={14}/></span>
            {" · "}
            <Star size={11} className="text-amber-300" />{" "}
            <span>{t.reward_xp.toLocaleString()} XP</span>
          </div>
          <div className="flex items-center gap-2">
            <Trophy size={12} className="text-[var(--muted)]" /> Capture:{" "}
            <span>{t.catch_chance}%</span>
          </div>
        </div>
      </div>
      {!locked && (
        <div className="mt-3 text-[10px] text-[var(--teal)] uppercase tracking-wider flex items-center gap-1">
          {cleared ? <>Replay <Swords size={10} /></> : <>Challenge <Swords size={10} /></>}
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pal picker
// ---------------------------------------------------------------------------
function PalPicker({ tower, pals, busy, cooldownSeconds, onPick, onCancel }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return pals;
    const needle = q.toLowerCase();
    return pals.filter((p) =>
      (p.name || "").toLowerCase().includes(needle) ||
      (p.nickname || "").toLowerCase().includes(needle)
    );
  }, [pals, q]);

  const onCooldown = cooldownSeconds > 0;

  return (
    <div data-testid="pal-picker">
      <button onClick={onCancel} className="btn btn-sm btn-secondary mb-4" data-testid="pal-picker-back">
        <ChevronLeft size={12} /> Back to towers
      </button>
      <div className="glass-card p-5 mb-4">
        <div className="flex items-center gap-4">
          <img
            src={`${ASSETS}/${tower.image}`}
            alt={tower.boss_name}
            className="w-16 h-16 object-cover rounded-lg bg-black/30"
            onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
          />
          <div>
            <h2 className="text-2xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              Tower {tower.tower} — {tower.boss_name}
            </h2>
            <p className="text-xs text-[var(--muted)] mt-1">
              Boss Lv {tower.level}
              {tower.elements?.length ? (
                <> · Type: <span className="text-amber-300">{tower.elements.join(" / ")}</span></>
              ) : null}
              {" · "}Weakness:{" "}
              <span className="text-[var(--bone)]">{tower.weakness}</span>
              {" · "}Reward:{" "}
              <span className="glow-gold inline-flex items-center gap-1">{tower.reward_skulls.toLocaleString()} <ChilletCoinIcon size={14}/></span> + {tower.reward_xp.toLocaleString()} XP
            </p>
          </div>
        </div>
      </div>

      {onCooldown ? (
        <div className="glass-card p-5 text-center text-sm" data-testid="pal-picker-cooldown">
          On cooldown for{" "}
          <span className="glow-teal font-semibold">{fmtMmSs(cooldownSeconds)}</span>
          {" "}— come back when your pals have rested.
        </div>
      ) : (
        <>
          <div className="mb-4">
            <input
              className="input"
              placeholder="Search your roster…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="pal-picker-search"
            />
          </div>
          {pals.length === 0 ? (
            <div className="glass-card p-8 text-center text-[var(--muted)] text-sm">
              You don't own any battle-ready pals yet. Capture some in the wild or breed via Pal Game first.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map((p) => (
                <button
                  key={p.name}
                  onClick={() => onPick(p.name)}
                  disabled={busy}
                  className="glass-card p-3 text-center hover:bg-white/5 transition-colors"
                  data-testid={`pal-pick-${p.name}`}
                >
                  <div className="w-20 h-20 mx-auto mb-2 flex items-center justify-center">
                    <PalPortrait name={p.name} size="w-20 h-20" />
                  </div>
                  <div className="text-xs font-medium truncate">{p.nickname || p.name}</div>
                  <div className="text-[10px] text-[var(--muted)]">Lv {p.level}</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Battle Arena — HP bars, skill buttons, log
// ---------------------------------------------------------------------------
function BattleArena({ battle, tower, busy, onSkill, onExit, logRef }) {
  const won = battle.result?.won;

  // Battle summary stats — computed from the full combat log so the
  // victory/defeat card can show "3 super-effective hits, biggest hit
  // 415" at a glance instead of forcing the player to scroll the log.
  const summary = useMemo(() => {
    const log = battle.log || [];
    const stats = {
      turns: 0,
      playerDmgDealt: 0,
      bossDmgDealt: 0,
      biggestHit: 0,
      biggestHitSkill: null,
      crits: 0,
      superEffective: 0,
      notEffective: 0,
    };
    for (const e of log) {
      stats.turns = Math.max(stats.turns, e.turn || 0);
      if (e.actor === "player") {
        stats.playerDmgDealt += e.damage || 0;
        if ((e.damage || 0) > stats.biggestHit) {
          stats.biggestHit = e.damage;
          stats.biggestHitSkill = e.skill;
        }
        if (e.crit) stats.crits += 1;
        if (e.effectiveness === "super_effective") stats.superEffective += 1;
        if (e.effectiveness === "not_effective") stats.notEffective += 1;
      } else {
        stats.bossDmgDealt += e.damage || 0;
      }
    }
    return stats;
  }, [battle.log]);

  return (
    <div data-testid="battle-arena">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          Tower {tower.tower} — {tower.boss_name}
        </h2>
        {battle.finished && (
          <button onClick={onExit} className="btn btn-sm" data-testid="battle-exit">
            <ChevronLeft size={12} /> Done
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Combatant
          label="Your Pal"
          name={battle.player.nickname || battle.player.name}
          palName={battle.player.name}
          level={battle.player.level}
          hp={battle.player.hp}
          hpMax={battle.player.hp_max}
          elements={battle.player.elements}
          isWinner={battle.finished && won}
        />
        <Combatant
          label="Boss"
          name={tower.boss_name}
          towerImage={tower.image}
          level={tower.level}
          hp={battle.boss.hp}
          hpMax={battle.boss.hp_max}
          elements={tower.elements || []}
          isLoser={battle.finished && won}
          accent="danger"
        />
      </div>

      <div className="glass-card p-4 mb-4">
        <h4 className="label">Skills</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {battle.player.skills.map((s, idx) => (
            <button
              key={idx}
              onClick={() => onSkill(idx)}
              disabled={busy || battle.finished}
              className="btn btn-sm"
              data-testid={`skill-${idx}`}
            >
              <Zap size={12} /> {s.name}
              <span className="text-[10px] ml-1 opacity-70">{s.power} pwr</span>
            </button>
          ))}
        </div>
      </div>

      <div className="glass-card p-4" ref={logRef} style={{ maxHeight: 260, overflowY: "auto" }} data-testid="battle-log">
        <h4 className="label">Combat Log</h4>
        {(battle.log || []).length === 0 && (
          <div className="text-xs text-[var(--muted)]">Pick a skill to begin the battle.</div>
        )}
        <ul className="space-y-1 text-xs">
          {(battle.log || []).map((e, i) => (
            <li key={i} className={e.actor === "player" ? "text-[var(--teal)]" : "text-rose-300"}>
              <span className="text-[var(--muted)] mr-1">T{e.turn}</span>
              <span className="font-semibold">
                {e.actor === "player" ? "You" : "Boss"}
              </span>{" "}
              used <strong>{e.skill}</strong> for{" "}
              <span className="glow-gold">{e.damage}</span>
              {e.crit ? <span className="text-amber-300"> · CRIT</span> : null}
              {e.effectiveness === "super_effective" && <span className="text-emerald-300"> · super effective!</span>}
              {e.effectiveness === "not_effective" && <span className="text-[var(--muted)]"> · not very effective</span>}
            </li>
          ))}
        </ul>
      </div>

      {battle.finished && battle.result && (
        <div
          className={`mt-4 glass-card p-6 text-center ${
            battle.result.won ? "border border-[var(--teal)]/40" : "border border-rose-500/30"
          }`}
          data-testid="battle-result"
        >
          {battle.result.won ? (
            <>
              <Trophy size={36} className="text-amber-300 mx-auto mb-2" />
              <h3 className="text-2xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
                Tower {tower.tower} Cleared
              </h3>
              <p className="text-sm mt-2">
                <span className="glow-gold inline-flex items-center gap-1">+{battle.result.skulls.toLocaleString()} <ChilletCoinIcon size={14}/></span>
                {" "}·{" "}
                <span className="text-amber-300">+{battle.result.xp.toLocaleString()} XP</span>
              </p>
              {battle.result.captured && (
                <p className="text-sm mt-2 glow-teal">
                  You captured <strong>{battle.result.boss_pal_name}</strong>!
                </p>
              )}
            </>
          ) : (
            <>
              <Skull size={36} className="text-rose-400 mx-auto mb-2" />
              <h3 className="text-2xl" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
                Your Pal Was Defeated
              </h3>
              <p className="text-xs text-[var(--muted)] mt-2">
                {battle.result.cooldown_seconds > 0
                  ? <>Cooldown {fmtMmSs(battle.result.cooldown_seconds)} — heal up and try again.</>
                  : "No cooldown — try a different pal."}
              </p>
            </>
          )}

          {/* Battle summary — derived from the combat log on the
              client so the server can stay dumb. Surfaces the bits a
              player would otherwise have to scroll the log to count. */}
          {summary.turns > 0 && (
            <div
              className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-left"
              data-testid="battle-summary"
            >
              <SummaryStat label="Turns" value={summary.turns} />
              <SummaryStat
                label="Biggest Hit"
                value={summary.biggestHit > 0 ? summary.biggestHit.toLocaleString() : "—"}
                sub={summary.biggestHitSkill || null}
                accent="gold"
              />
              <SummaryStat
                label="Damage Dealt / Taken"
                value={`${summary.playerDmgDealt.toLocaleString()} / ${summary.bossDmgDealt.toLocaleString()}`}
              />
              <SummaryStat
                label="Type Hits"
                value={
                  summary.superEffective > 0 || summary.notEffective > 0
                    ? `${summary.superEffective} super · ${summary.notEffective} weak`
                    : "all neutral"
                }
                sub={summary.crits > 0 ? `${summary.crits} crit${summary.crits === 1 ? "" : "s"}` : null}
                accent={summary.superEffective > summary.notEffective ? "teal" : null}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, sub, accent }) {
  const accentCls =
    accent === "gold" ? "glow-gold"
    : accent === "teal" ? "glow-teal"
    : "text-[var(--bone)]";
  return (
    <div className="bg-black/30 rounded-lg p-3" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
      <div className={`text-base font-semibold mt-0.5 ${accentCls}`}>{value}</div>
      {sub ? <div className="text-[10px] text-[var(--muted)] mt-0.5">{sub}</div> : null}
    </div>
  );
}

function Combatant({ label, name, palName, towerImage, level, hp, hpMax, elements, isWinner, isLoser, accent }) {
  const pct = Math.max(0, Math.min(100, Math.round((hp / Math.max(1, hpMax)) * 100)));
  const colour =
    pct > 60 ? "bg-emerald-500" : pct > 30 ? "bg-amber-400" : "bg-rose-500";

  // Quick "ouch" flash whenever HP drops — gives the player immediate
  // visual feedback that the attack landed, not just a number that
  // ticks down silently. We compare against the previous render via
  // a ref, then strobe a CSS class for 400 ms.
  //
  // Bugfix 2026-02-22: `prevHpRef.current` must be updated on EVERY
  // run, including the damage branch. The previous version only
  // assigned in the else-path, which left the ref pinned at the
  // initial hp value — subsequent heals (hp > prev) would compare
  // against the stale starting hp and never re-prime, and a later
  // heal followed by another hit could miss-fire the flash entirely.
  const prevHpRef = useRef(hp);
  const [hit, setHit] = useState(false);
  useEffect(() => {
    const dropped = hp < prevHpRef.current;
    prevHpRef.current = hp;
    if (!dropped) return undefined;
    setHit(true);
    const t = setTimeout(() => setHit(false), 400);
    return () => clearTimeout(t);
  }, [hp]);

  return (
    <div
      className={`glass-card p-4 transition-opacity ${isLoser ? "opacity-50" : ""} ${isWinner ? "ring-1 ring-[var(--teal)]/40" : ""} ${hit ? "combatant--hit" : ""}`}
      data-testid={`combatant-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-black/40 rounded-lg overflow-hidden">
          {towerImage ? (
            <img
              src={`${ASSETS}/${towerImage}`}
              alt={name}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
            />
          ) : (
            <PalPortrait name={palName || name} size="w-20 h-20" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
          <div className="text-base font-semibold truncate">{name}</div>
          <div className="text-xs text-[var(--muted)]">
            Lv {level}
            {elements?.length ? <> · {elements.join(" / ")}</> : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Heart size={12} className={accent === "danger" ? "text-rose-400" : "text-emerald-400"} />
        <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
          <div
            className={`h-full ${colour} transition-all duration-500`}
            style={{ width: `${pct}%` }}
            data-testid="combatant-hp-bar"
          />
        </div>
        <span className={`font-mono text-[10px] ${hit ? "text-rose-300" : ""}`}>{hp}/{hpMax}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------
function fmtMmSs(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
